import { React, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { NavLink } from 'react-router-dom';
import Markdown from 'react-markdown';
import './StartUpGuide.css';

function StartUpGuide() {
    const [sidebar, setSidebar] = useState(false);
    
    const toggleSidebar = () => {
        setSidebar(!sidebar);
    };

    // Animation for motion divs
    const fadeInUp = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    // Markdowns for startup guide. DO NOT INDENT
    const signingUp = `
Go to the [beta.enablegames.com](beta.enablegames.com) webpage. This is our user portal.

#### Choose Your Account Type
![Sign-up Page](/images/startup-guide/signupPage.png)

There are two types of accounts:
- **Home** account (for an individual **Player**). Click on the **Sign up!** link under **Player** to create an account.  You will need to provide your email.  If you do not receive a verification email, please contact **support@enablegames.com**.

![Home Account Sign-up](/images/startup-guide/signupHome.png)

- **Clinic** account (for a **Care Provider** to manage several Players)
  - For security purposes during this beta, when you sign up for a new clinic account, your account will not be made active until you email **support@enablegames.com** for us to manually activate it.  If you already have a clinic account, you can add new player accounts by yourself without contacting us by clicking “Create Sponsored Player” on your clinic web portal.

![Sign-up Page](/images/startup-guide/signupClinic.png)

Fill in your information, and the Beta Key that should be supplied to you by email. If you do not have a Beta Key, please contact **support@enablegames.com**.

You will then be taken to the enAbleGames Player or Care Provider Portal.
`;

    const connectingEquipment = `
1. Set your Computer on a flat surface in an area that has enough room for game playing (at least 6 feet wide x 3-4 feet deep)

2. Place the Kinect sensor, Astra sensor, or external webcam near the screen. The screen may be your laptop screen or a flat screen TV if one is available at your clinic or home site. The Kinect should be placed below or above the TV, or near your laptop.

3. Plug in your computer. The sensors work best with the computer on full power.

4. Plug in your sensor.
- For **Kinect V2** (Xbox One Kinect)
  - Plugin the Kinect sensor cord into the Kinect Adapter for Windows.
  - Plug in the Kinect Adaptor’s USB cable to the BLUE USB port (USB-3 port) of the Computer. You must have a USB-3 port to use a Kinect V2 sensor.
  - Plug in the Kinect Adaptor to the power strip. Make sure the white light on the Kinect adaptor power block is on.
- For **Orbbec Astra**
  - Plug the Astra into your USB port (must be a USB-2 or USB-3 port).
- For **Kinect 1** (Xbox 360 Kinect, or Kinect for Windows)
  - Plugin the Kinect sensor cord into the Xbox 360 Kinect Sensor USB AC Adapter
  - Plug in the Kinect Adaptor’s USB cable to the BLUE USB port (USB-3 port) of the Computer. You must have a USB-3 port to use a Kinect sensor.
  - Plug in the Kinect Adaptor to the power strip. Make sure the white light on the Kinect adaptor power block is on.
- For **Leap Motion** Sensor
  - Plug in the Leap sensor into a USB-2 port.
5. If you are using a separate flat screen (not the laptop screen), plug in the HDMI cable to the Computer HDMI port and plug in the other end to the flat screen HDMI port.

6. Now you are ready to power on the game computer!
`;

    const installDrivers = `
#### Kinect V2:

Download Kinect for Windows Runtime 2.0 from Official Microsoft Download Center (add link)

#### Kinect V1:

Download Kinect for Windows Runtime v1.8 from Official Microsoft Download Center (add link)

#### Orbbec Astra:

1. Download the Orbbec Driver zip file: astra-win32-driver-4.3.0.22

Mirror: https://drive.google.com/file/d/1xG4HybH0gM69ipnma3_kJrpPfyC41Y-C/view?usp=share_link

2. Unzip the downloaded file and run SensorDriver_V4.3.0.4.exe to install the Orbbec Astra driver.

3. Connect the Orbbec Astra sensor. If the driver is installed correctly, you should see it in the Device Manager, under ‘Orbbec’.

#### Leap Motion:

enAblegames uses V2 of the Leap software. This can be installed via the appropriate version for your operating system in this link: [Leap V2 for Windows, Mac, and Linux](https://drive.google.com/drive/folders/1WGvoigvcOkFpvLrlBuZSnq6GdxdnYuQS)
- Pick desktop mode for installation
- Uncheck box for Leap Application

If the LeapMotion still not detect your hands initially and you are sure your device is connected, run the dpinst64.exe program in C:\\Program Files (x86)\\Leap Motion\\Core Services\\Drivers.  If this does not fix the issue, follow the below steps:

1. Open up services.msc and pause or stop LeapService

2. Navigate to your Leap Motion software installation folder and go to the Core Services folder (default C:\\Program Files (x86)\\Leap Motion\\Core Services)

3. Backup LeapSvc.exe and LeapSvc64.exe in a separate folder

4. For V2 users, download and unzip this file6.6k (add link) into the Core Services folder, replacing LeapSvc.exe and LeapSvc64.exe

5. Return to services.msc and restart LeapService

6. Launch a Leap Motion enabled application and test!
`;

    const installApp = `
You are now ready to install the enAbleGames App. The enAbleGames App is the main application, and has many roles:
- **Installer**: Downloads and installs all games.
- **Patcher**: Lets you know when updates to games are available, and installs updates if desired.
- **Launcher**: Displays available games and launches (runs) selected game.
- **Body Tracker**: Connects to body tracking devices, displays body motions using an avatar, and passes body data to games.
- **Login Manager**: Login once to the app, and you won’t have to log in to the games individually.

The App must be running for the games to work, since the app handles logging in and passing the body motion data to the games. This includes VR games running on a phone.

#### Get the enAbleGames App from the Portal

You will be provided a link to download the beta version of the enAbleGames installer via email. To request the enAbleGames installer, send an email to **support@enablegames.com**.

Click the **Download** button to download and run the installer (enAbleGames_setup.exe). You may get a message about it being an unrecognized app and only see a **Don’t run** button:

![Windows protected your PC](/images/startup-guide/setupApp1.png)

This is because the installer is not a signed Windows app, but that is ok. Click on the **More Info** text and a **Run anyway** button will appear.

![Click run anyway](/images/startup-guide/setupApp2.png)

Click **Run anyway** and follow the setup instructions as described below.

First, you will see a message about choosing your installation directory. By default, enAbleGames is installed under your user **Documents** directory. If you want to install it to your **Program Files** directory, the installer needs to be run with Administrator access. To do this, right-click on the installer, and select “**Run as administrator**” and create/select the **C:/Program Files/enAbleGames** folder. Otherwise, please install under one of your user directories, such as Documents, your Desktop, or the AppLocal folder (i.e. C:/users/myname/AppLocal)

Make sure **Create a desktop shortcut** is checked.

![Check create a desktop shortcut](/images/startup-guide/setupAppCreateShortcut.png)

After installing the Launcher, log in using your enAbleGames account.  It may take a few minutes to check for updates. When the Launcher asks if you would like to download updates, select “yes”.  The Launcher will open the Patcher application in a new window.

If you have trouble logging in, check that your computer’s firewall settings and make sure to allow enAbleGames to connect online in the Inbound Rules section. Each enablegames app should have a green checkmark next to it, as shown below.

![Check firewall settings](/images/startup-guide/setupAppFirewall.png)

After the Patcher is finished, the Launcher will re-open. Log in again when you see the login screen, and you will see the Library page.
`;

    const bodyTracking = `
There are 4 types of controls in the enAbleGames Launcher:
- Kinect/Astra
- Webcam Tracking
- The enableServer app
- Color Tracking.

#### Kinect/Astra

![Kinect/Astra](/images/startup-guide/kinectAndAstra.png)

This method of control offers some of the most accurate body controls, though it requires additional hardware to use.

To use a Kinect or Astra device, you will need to install the appropriate drivers to your computer. See the **Installing Device Drivers** section above for further information.

To start using Kinect/Astra, press the Start Kinect/Astra button. The body tracking can be adjusted with the 3 buttons above.
- If you are experiencing lag from the Kinect, the **Smooth Controls** button will interpolate the poses between what is received from the Kinect, which will give the movement a more ogranic feeling.
- If the Kinect is sending data at a rate that is too high for your system to process without lag, you can click Skip Frames.
- If there are multiple people being tracked on the Kinect camera view, press **Switch Player** to cycle between tracked individuals.  The primary detected player is colored yellow in the camera view.

#### Webcam Tracking
![Webcam Tracking](/images/startup-guide/webcamTracking.png)

This method of control can be used on any piece of hardware with a camera, though it also requires a GPU card. It may slow performance depending on the capabilities of user’s device.

Webcam Tracking can be activated by pressing the “Start Webcam Tracking” button on the Body Tracking page of the enAbleGames Launcher. Make sure your webcam is active before using this mode.

By checking the “Only Move Upper Body” option, you can reduce latency in body tracking, though you will not be able to use your legs.

*If webcam tracking does not work initially, try restarting the Launcher.*

#### enableServer App

![enAbleServer App](/images/startup-guide/enAbleServer.png)

This method requires a phone or tablet in addition to your computer, but it may reduce latency in games since the body pose processing is not being handled by your computer.

To set up the enableServer App, email **support@enablegames.com** with your Apple ID and request the enableServer App.

If you have the enableServer app installed on a secondary device, you can control the Launcher remotely. You will need to connect your secondary device to the same wifi as the device running the Launcher.

**Connect to the Launcher from the app**

Using the button on the bottom left of enableServer, add the device and input the IP Address from the Launcher, which can be found on the right side of the Body Tracking tab. You can also manually scan the QR code from the Launcher in the app.

The enableServer app can also communicate directly with games if the Launcher is not running. The connection QR code or IP address can be found through a game’s pause menu.

#### Color Tracking

![Color Tracking](/images/startup-guide/colorTracking.png)

This method is the least computationally intensive of any control mode, though it is highly limited in the types of motions the player can make. It is not able to calculate accurate elbow motions or lower-body motions since it only calculates based on hand positions.

Color Tracking can be started with the Start Color Tracking button in the Body Tracking menu. Make sure your webcam is active before using this mode. The body will track two colors from your webcam, which you choose by clicking the left and right mouse buttons on the screen.
`;

    return (
        <div className="startup-guide-page">
            <div className="startup-guide-container">
                <div className={`sidebar ${!sidebar ? "collapsed" : ""}`}>
                    <button onClick={toggleSidebar} className="sidebar-btn">
                        {sidebar ? <FontAwesomeIcon icon={faArrowRight} /> : <FontAwesomeIcon icon={faArrowLeft} />}
                    </button>
                    <div className="sidebar-menu">
                        <h2>Startup Guide</h2>
                        <p><a href="#signing-up">Signing Up for enAbleGames</a></p>
                        <p><a href="#connecting-equipment">Connecting the Equipment</a></p>
                        <p><a href="#installing-device-drivers">Installing Device Drivers</a></p>
                        <p><a href="#installing-app">Installing the enAbleGames App</a></p>
                        <p><a href="#installing-and-playing">Installing and Playing Games</a></p>
                        <p><a href="#body-tracking">Controlling Games with Body Tracking</a></p>
                        <p><a href="#rules-and-controls">Modifying Game Rules and Controls</a></p>
                        <p><a href="#troubleshooting">Troubleshooting</a></p>
                    </div>
                </div>
                <motion.div className="startup-header"
                {...fadeInUp}
                transition={{ delay: 0.3 }}>
                    <h2>Startup Guide</h2>
                    <p>PLEASE READ FIRST: If you are using body sensors (i.e. Kinect, Astra, Leap, etc.), please connect your equipment and install all Device Drivers BEFORE running the enAbleGames App.  The App needs drivers to be installed in order to track your body. The holistic webcam feature does not require any drivers and uses your standard web camera, as does the low-power color-tracking feature for hand-tracking. See the Installing Device Drivers section below.</p>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.4 }}>
                    <h3 id="signing-up">Signing Up for enAbleGames</h3>
                    <Markdown>{signingUp}</Markdown>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.5 }}>
                    <h3 id="connecting-equipment">Connecting the Equipment</h3>
                    <Markdown>{connectingEquipment}</Markdown>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.6 }}>
                    <h3 id="installing-device-drivers">Installing Device Drivers</h3>
                    <p>Unless you are using your webcam, every motion sensor you want to use needs a device driver.  Download and install the appropriate sensor drivers. See our <NavLink to="/support">Support</NavLink> page for latest driver information, or contact support@enablegames.com for help.</p>
                    <Markdown>{installDrivers}</Markdown>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.7 }}>
                    <h3 id="installing-app">Installing the enAbleGames App</h3>
                    <Markdown>{installApp}</Markdown>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.8 }}>
                    <h3 id="installing-and-playing">Installing and Playing Games</h3>
                    <p>From the left side of the screen, select the game you would like to download. Click the “Update Game” button in the top-left corner of the screen. It will launch the Patcher in another window.</p>
                    <img src="/images/startup-guide/installPlayGames.png" alt="Update Game"></img>
                    <p>When the Patcher finishes installing your game, refresh the Launcher and press the Play button in the top-left corner to play your game.</p>
                    <p>Instructions for the games can be found on our <a href="https://www.enablegames.com/support">Support</a> page.</p>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 0.9 }}>
                    <h3 id="body-tracking">Controlling Games with Body Tracking</h3>
                    <Markdown>{bodyTracking}</Markdown>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 1.0 }}>
                    <h3 id="rules-and-controls">Modifying Game Rules and Controls</h3>
                    <p>In your game, select “Settings” to choose the control mode for your session.  You can also adjust other settings for the games, such as duration of play session, or the initial difficulty of the level.</p>
                    <img src="/images/startup-guide/rulesAndControls1.png" alt="Update Game"></img>
                    <p>Once the game has started, you can view the game’s reading on your body data by clicking the human figure in the bottom corner.</p>
                    <img src="/images/startup-guide/rulesAndControls2.png" alt="Update Game"></img>
                    <img src="/images/startup-guide/rulesAndControls3.png" alt="Update Game"></img>
                </motion.div>
                <motion.div className="guide-card"
                {...fadeInUp}
                transition={{ delay: 1.1 }}>
                    <h3 id="troubleshooting">Troubleshooting</h3>
                    <p>Please check the <NavLink to="/support">Support</NavLink> page FAQ if you have any problems or questions.</p>
                </motion.div>
            </div>
        </div>
    );
}

export default StartUpGuide;